import './Pagination.css';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../../i18n/i18n';
import { ApiResource, useApi } from '../../state/api';
import { ApiCategoryMetaData, Paginated } from '../../types';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { Loader } from '../Loader/Loader';
import { toQuery, useQuery } from '../Routes';
import { Helmet } from 'react-helmet-async';
import { ReactNode } from 'react';

type PaginationProps = {
	path: string;
	params: Record<string, string | number>;
	total: number;
	offset: number;
	limit: number;
	maxPages?: number;
};
export const Pagination = ({ path, params, total, offset, limit, maxPages = 10 }: PaginationProps): JSX.Element => {
	const num = Math.ceil(total / limit);
	const page = 1 + Math.ceil(offset / limit);
	let pages: number[] = [];
	for (let i = 1; i <= num; i++) {
		pages.push(i);
	}

	if (pages.length > maxPages) {
		pages = pages.slice(0, maxPages);
	}

	if (total <= limit) return <></>;

	return (
		<div className="pagination">
			{pages.map((i) => (
				<Link
					className={i === page ? 'active' : ''}
					to={`${path}?${toQuery({
						...params,
						offset: limit * (i - 1),
						limit,
					})}`}
					key={i}
				>
					{i}
				</Link>
			))}
		</div>
	);
};

type AutoPaginationProps = {
	data: { total: number; limit: number; offset: number };
};
export const AutoPagination = ({ data: { total, limit, offset } }: AutoPaginationProps): JSX.Element => {
	const path = useLocation().pathname;
	const params = useQuery<{ offset?: string; limit?: string }>();
	const maxPages = 10;
	delete params.offset;
	delete params.limit;
	return <Pagination path={path} params={params} total={total} limit={limit} offset={offset} maxPages={maxPages} />;
};

const pageSize = 24;

type Paged = { limit: number; offset: number };

type PagesProps<TRes, TParams extends Paged> = {
	className?: string;
	onRender?: (data: TRes) => void;
	resource: ApiResource<TRes, TParams>;
	params: Unpaginated<TParams>;
	preamble?: ReactNode;
	buildChildren: (data: TRes) => ReactNode[];
	descriptionData?: ApiCategoryMetaData;
};
export function Pages<TRes extends Paginated, TParams extends Paged>({
	className = '',
	resource,
	params,
	preamble,
	onRender,
	buildChildren,
}: PagesProps<TRes, TParams>) {
	const t = useTranslation().generic;
	const path = useLocation().pathname;
	const query = useQuery<{ page?: string }>();
	const pageCount = +(query.page ?? '1');
	const { data } = useApi(resource, {
		...params,
		limit: pageSize,
		offset: 0,
	});
	const showLoadMore = data && data.total > pageSize * pageCount;

	let pages: JSX.Element[] = [];
	for (var i = 0; i < pageCount; i++)
		pages.push(
			<Page
				key={i}
				resource={resource}
				params={params}
				index={i}
				buildChildren={buildChildren}
				onRender={onRender}
			/>
		);

	return (
		<>
			{pageCount > 1 && (
				<Helmet>
					<link
						rel="prev"
						href={path + (pageCount > 2 ? '?' + toQuery({ ...query, page: pageCount - 1 }) : '')}
					/>
					{showLoadMore && <link rel="next" href={path + '?' + toQuery({ ...query, page: pageCount + 1 })} />}
				</Helmet>
			)}

			<div className={'product-listing ' + className}>
				{preamble}
				{pages}
			</div>

			{showLoadMore && (
				<div className="load-more">
					<Link replace to={path + '?' + toQuery({ ...query, page: pageCount + 1 })}>
						{t.loadMore}
					</Link>
					<div className="progressbar">
						<div className="progress-blend" />
						<div
							className="progress"
							style={{ width: `${(100 * (pageCount * pageSize)) / data.total}%` }}
						/>
					</div>
					<div>
						{pageCount * pageSize} {t.of} {data.total}
					</div>
				</div>
			)}
		</>
	);
}

type Unpaginated<T extends Paged> = Omit<Omit<T, 'limit'>, 'offset'>;

type PageProps<TRes, TParams extends Paged> = {
	resource: ApiResource<TRes, TParams>;
	params: Unpaginated<TParams>;
	index: number;
	onRender?: (data: TRes) => void;
	buildChildren: (data: TRes) => ReactNode[];
};
export function Page<TRes, TParams extends Paged>({
	resource,
	params,
	index,
	onRender,
	buildChildren,
}: PageProps<TRes, TParams>): ReactNode[] {
	const { data, error } = useApi(resource, {
		...params,
		limit: pageSize,
		offset: index * pageSize,
	} as TParams);
	if (error && !data) return [<ErrorMessage key="error" />];
	if (data) {
		onRender?.(data);
		return buildChildren(data);
	} else
		return [
			<Loader size="box" key="a" />,
			<Loader size="box" key="b" />,
			<Loader size="box" key="c" />,
			<Loader size="box" key="d" />,
			<Loader size="box" key="e" />,
		];
}
